// src/services/smartReminderService.js
import { getData, saveData } from './storageService';

export class SmartReminderService {
  constructor() {
    this.reminders = [];
    this.loadReminders();
  }

  async loadReminders() {
    try {
      this.reminders = await getData('technician_reminders') || [];
    } catch (error) {
      console.error('Error loading reminders:', error);
      this.reminders = [];
    }
  }

  async saveReminders() {
    await saveData('technician_reminders', this.reminders);
  }

  // Автоматичне створення нагадувань на основі історичних даних
  async generateSmartReminders() {
    const reports = await getData('technician_reports') || [];
    const maintenance = await getData('maintenance') || [];
    const equipment = await getData('equipment') || [];

    const newReminders = [];

    // 1. Нагадування про регулярне ТО
    equipment.forEach(machine => {
      const lastMaintenance = maintenance
        .filter(m => m.machineId === machine.id)
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      if (lastMaintenance) {
        const lastMaintenanceDate = new Date(lastMaintenance.date);
        const daysSinceMaintenance = (new Date() - lastMaintenanceDate) / (1000 * 60 * 60 * 24);

        if (daysSinceMaintenance > 30) { // ТО кожні 30 днів
          newReminders.push({
            id: `maintenance_${machine.id}_${Date.now()}`,
            type: 'preventive_maintenance',
            title: `Профілактичне обслуговування - ${machine.name}`,
            message: `Час для планового ТО обладнання ${machine.name}`,
            priority: 'medium',
            dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Наступний тиждень
            machineId: machine.id,
            createdAt: new Date().toISOString(),
            status: 'pending'
          });
        }
      }
    });

    // 2. Нагадування на основі повторюваних проблем
    const recentReports = reports
      .filter(r => new Date(r.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
      .slice(0, 20);

    const issueFrequency = {};
    recentReports.forEach(report => {
      if (report.issueTypes) {
        report.issueTypes.forEach(issue => {
          issueFrequency[issue] = (issueFrequency[issue] || 0) + 1;
        });
      }
    });

    Object.entries(issueFrequency)
      .filter(([_, count]) => count >= 3) // Якщо проблема повторюється 3+ рази
      .forEach(([issue, count]) => {
        newReminders.push({
          id: `recurring_${issue}_${Date.now()}`,
          type: 'recurring_issue',
          title: `Повторювана проблема: ${this.getIssueTitle(issue)}`,
          message: `Проблема "${this.getIssueTitle(issue)}" зустрічалась ${count} разів за останній місяць. Рекомендується детальний аналіз.`,
          priority: 'high',
          dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 дні
          issueType: issue,
          occurrenceCount: count,
          createdAt: new Date().toISOString(),
          status: 'pending'
        });
      });

    // 3. Нагадування про сезонні проблеми
    const currentMonth = new Date().getMonth();
    const seasonalReminders = this.generateSeasonalReminders(currentMonth);
    newReminders.push(...seasonalReminders);

    // Додаємо нові нагадування
    this.reminders = [...this.reminders, ...newReminders];
    await this.saveReminders();

    return newReminders;
  }

  getIssueTitle(issueType) {
    const issueTitles = {
      'low_pressure': 'Низький тиск',
      'low_water_level': 'Низький рівень води',
      'leak_detected': 'Виявлено витік',
      'broken_part': 'Зламана деталь',
      'corrosion': 'Корозія',
      'low_temperature': 'Низька температура'
    };
    return issueTitles[issueType] || issueType;
  }

  generateSeasonalReminders(month) {
    const reminders = [];

    // Літо - перевірка систем охолодження
    if (month >= 5 && month <= 7) {
      reminders.push({
        id: `seasonal_cooling_${Date.now()}`,
        type: 'seasonal',
        title: 'Сезонна перевірка: Системи охолодження',
        message: 'Літній період - час перевірити системи охолодження та кондиціювання',
        priority: 'medium',
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        season: 'summer',
        createdAt: new Date().toISOString(),
        status: 'pending'
      });
    }

    // Зима - перевірка опалення
    if (month >= 11 || month <= 1) {
      reminders.push({
        id: `seasonal_heating_${Date.now()}`,
        type: 'seasonal',
        title: 'Сезонна перевірка: Системи опалення',
        message: 'Зимовий період - час перевірити системи опалення та теплоізоляцію',
        priority: 'medium',
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        season: 'winter',
        createdAt: new Date().toISOString(),
        status: 'pending'
      });
    }

    return reminders;
  }

  // Отримання активних нагадувань
  getActiveReminders() {
    return this.reminders.filter(reminder => 
      reminder.status === 'pending' && 
      new Date(reminder.dueDate) > new Date()
    );
  }

  // Отримання прострочених нагадувань
  getOverdueReminders() {
    return this.reminders.filter(reminder => 
      reminder.status === 'pending' && 
      new Date(reminder.dueDate) <= new Date()
    );
  }

  // Позначити нагадування як виконане
  async markAsCompleted(reminderId) {
    const reminder = this.reminders.find(r => r.id === reminderId);
    if (reminder) {
      reminder.status = 'completed';
      reminder.completedAt = new Date().toISOString();
      await this.saveReminders();
    }
  }

  // Отримати нагадування за пріоритетом
  getRemindersByPriority(priority) {
    return this.getActiveReminders().filter(r => r.priority === priority);
  }

  // Очистити старі нагадування
  async cleanupOldReminders() {
    const cutoffDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 днів
    this.reminders = this.reminders.filter(reminder => 
      new Date(reminder.createdAt) > cutoffDate
    );
    await this.saveReminders();
  }
}